/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Button, Modal, Select } from 'antd'
import moment from 'moment'

import Chart from 'react-chartjs-2'
import 'chartjs-plugin-zoom'

import R from 'ramda'

const { Option } = Select

const defaultChartState = {
  type: 'line',
  xIdent: undefined,
  yIdentList: [],
  xAxesType: 'category',
}

const AXES_TYPES = ['category', 'linear', 'logarithmic', 'time']

const CHART_TYPES = [
  'line',
  'bar',
  'horizontalBar',
  'radar',
  'doughnut',
  'polarArea',
  'bubble',
  'pie',
  'scatter',
]

/**
 * ECMA2016 / ES6
 */
const convertRGBToRGBA = (rgb, opacity) => {
  return rgb.replace(/rgb/i, 'rgba').replace(/\)/i, `,${opacity})`)
}

const backgrounds = [
  'rgb(255,215,0)',
  'rgb(255,177,78)',
  'rgb(250,135,117)',
  'rgb(234,95,148)',
  'rgb(205,52,181)',
  'rgb(157,2,215)',
  'rgb(0,0,255)',
  'rgb(0,255,0)',
  'rgb(255,0,0)',
]

const backgroundsWithOpacity = backgrounds.map(item =>
  convertRGBToRGBA(item, 0.2)
)

const getDefaultAxesTypeFromColumn = col => {
  return col.dataType === 'DATE'
    ? 'time'
    : col.dataType === 'NUMBER'
    ? 'linear'
    : 'category'
}

const getDefaultChartTypeFromColumn = (col, data) => {
  return col.dataType === 'NUMBER'
    ? 'line'
    : col.dataType === 'NUMBER'
    ? 'line'
    : data.length < 10
    ? 'pie'
    : 'bar'
}

const supportedYAxes = columns => {
  return columns.filter(item => item.dataType === 'NUMBER')
}

const prepareChartStateFromColumns = (columns, data) => {
  if (columns && columns.length > 0) {
    const firstColumn = columns[0]
    const restColumns = supportedYAxes(columns.slice(1))
    return {
      type: getDefaultChartTypeFromColumn(firstColumn, data),
      xIdent: firstColumn.ident,
      yIdentList: restColumns.map(item => item.ident),
      xAxesType: getDefaultAxesTypeFromColumn(firstColumn),
    }
  }

  return defaultChartState
}

const ChartModal = ({ profile, setProfile, data, columns, hide }) => {
  const [chartState, setChartState] = useState(
    (profile.chartState && JSON.parse(profile.chartState)) ||
      prepareChartStateFromColumns(columns, data)
  )
  const [labels, setLabels] = useState([])

  const [dataSets, setDataSets] = useState([])

  const chartRef = useRef()

  const columnMap = useMemo(() => R.fromPairs(columns.map(c => [c.ident, c])), [
    columns,
  ])

  useEffect(() => {
    if (chartState.xIdent) {
      setLabels(
        data.map(item =>
          columnMap[chartState.xIdent].dataType === 'DATE'
            ? moment(item[chartState.xIdent]).toDate()
            : item[chartState.xIdent]
        )
      )
    }
  }, [columnMap, data, chartState.xIdent])

  const isSegmentBased = ['pie', 'doughnut', 'polarArea'].includes(
    chartState.type
  )
  useEffect(() => {
    if (chartState.yIdentList) {
      setDataSets(
        chartState.yIdentList.map((ident, index) => {
          return {
            label: ident,
            barPercentage: 0.5,
            barThickness: 6,
            maxBarThickness: 8,
            minBarLength: 2,
            backgroundColor: isSegmentBased
              ? backgrounds
              : backgroundsWithOpacity[index],
            data: isSegmentBased
              ? data.map(item => item[ident])
              : data.map(item => ({
                  x: item[chartState.xIdent],
                  y: item[ident],
                })),
          }
        })
      )
    }
  }, [
    columnMap,
    data,
    chartState.yIdentList,
    chartState.xIdent,
    isSegmentBased,
  ])

  const onOk = () => {
    setProfile({
      ...profile,
      chartState: JSON.stringify(chartState),
    })
    hide()
  }

  return (
    <Modal
      title="Chart"
      width="75vw"
      visible
      onOk={onOk}
      okText="Save"
      onCancel={hide}
      centered
      maskClosable={false}
    >
      <div>
        Type :
        <Select
          value={chartState.type}
          onChange={val => setChartState(state => ({ ...state, type: val }))}
          style={{ minWidth: 200, margin: '0px 5px' }}
        >
          {CHART_TYPES.map(item => {
            return (
              <Option key={item} value={item}>
                {item}
              </Option>
            )
          })}
        </Select>
        X-Axis :
        <Select
          value={chartState.xIdent}
          onChange={val => {
            const col = columnMap[val]
            setChartState(state => ({
              ...state,
              xIdent: val,
              xAxesType: getDefaultAxesTypeFromColumn(col),
            }))
          }}
          style={{ minWidth: 200, margin: '0px 5px' }}
        >
          {columns.map(item => {
            return (
              <Option key={item.ident} value={item.ident}>
                {item.display} ({item.dataType})
              </Option>
            )
          })}
        </Select>
        Axes Type :
        <Select
          value={chartState.xAxesType}
          onChange={val =>
            setChartState(state => ({ ...state, xAxesType: val }))
          }
          style={{ minWidth: 100, margin: '0px 5px' }}
        >
          {AXES_TYPES.map(item => {
            return (
              <Option key={item} value={item}>
                {item}
              </Option>
            )
          })}
        </Select>
        Graph For :
        <Select
          value={chartState.yIdentList}
          onChange={values =>
            setChartState(state => ({ ...state, yIdentList: values }))
          }
          style={{
            width: 400,
            margin: '0px 5px',
          }}
          mode="multiple"
          maxTagCount={3}
        >
          {supportedYAxes(columns).map(item => {
            return (
              <Option key={item.ident} value={item.ident}>
                {item.display} ({item.dataType})
              </Option>
            )
          })}
        </Select>
        <Button
          onClick={() => {
            if (chartRef.current) {
              chartRef.current.chartInstance.resetZoom()
            }
          }}
        >
          Reset Zoom
        </Button>
      </div>
      {/* {JSON.stringify({ data, columns })} */}
      <div>
        <Chart
          ref={chartRef}
          type={chartState.type}
          redraw
          options={{
            responsive: true,
            maintainAspectRatio: true,
            scales: {
              xAxes: [
                {
                  type: chartState.xAxesType,
                  // distribution: 'series',
                },
              ],
            },
            pan: {
              enabled: true,
              mode: 'x',
              speed: 10,
              threshold: 10,
            },
            zoom: {
              enabled: true,
              drag: false,
              mode: 'x',
              limits: {
                max: 10,
                min: 0.5,
              },
            },
          }}
          data={{
            labels: labels,
            datasets: dataSets,
          }}
        />
      </div>
    </Modal>
  )
}

export default ChartModal
